/**
 * 数字资产 · 资产详情抽屉
 * - 左:锁定图大图(点击 → Lightbox) + 维度详情
 * - 右:历史 generations 列表
 *   - 每轮可整轮删除(垃圾桶按钮)
 *   - 候选图悬停浮层:🔍 查看大图 / 🔒 切换为锁定
 * - 底部:重新生成
 *
 * 锁定 / 切锁 / 删除轮 后自动 invalidate 相关查询,UI 立即反映。
 */
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, RefreshCw, ImageIcon, Lock, Trash2, Maximize2, Pencil, Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toast';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { CreateAssetDialog } from './CreateAssetDialog';
import { Lightbox } from './Lightbox';
import type { BrainAsset, CharacterAttributes, LocationAttributes, PropAttributes } from './types';

interface Props {
  open: boolean;
  asset: BrainAsset | null;
  onClose: () => void;
  onChanged?: () => void;
}

const CHAR_LABELS: Record<keyof CharacterAttributes, string> = {
  gender: '性别',
  age: '年龄段',
  role: '角色定位',
  personality: '性格',
  preferences: '喜好/习惯',
  filmStyle: '影视风格',
  appearance: '外貌补充',
  extra: '其它',
  desire: '深层渴望',
  fear: '核心恐惧',
  flaw: '核心缺陷',
  contradiction: '人物矛盾',
  signatureBehavior: '标志性行为',
  subtext: '说话潜台词',
  speakingStyle: '语言风格',
  relations: '人物关系',
};

const LOC_LABELS: Record<keyof LocationAttributes, string> = {
  kind: '场景类型',
  atmosphere: '氛围',
  time: '时间段',
  lighting: '灯光风格',
  visualStyle: '视觉风格',
  keyElements: '关键元素',
  extra: '其它',
};

const PROP_LABELS: Record<keyof PropAttributes, string> = {
  category: '道具类别',
  material: '材质',
  style: '风格',
  size: '尺寸',
  visualStyle: '影视风格',
  variantName: '形象名称',
  variantDesc: '形象描述',
  appearance: '外观补充',
  extra: '其它',
};

export function AssetDetailDrawer({ open, asset, onClose, onChanged }: Props) {
  const qc = useQueryClient();
  const [regenOpen, setRegenOpen] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lockTarget, setLockTarget] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ runId: string; idx: number } | null>(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');

  const { data: fresh } = useQuery({
    queryKey: ['brain', 'asset', asset?.assetId],
    enabled: open && !!asset?.assetId,
    queryFn: () => api.get(`brain/assets/${asset!.assetId}`).json<BrainAsset>(),
  });

  const a = fresh ?? asset;

  const lockMut = useMutation({
    mutationFn: async (vars: { candidateId: string }) => {
      return await api
        .post(`brain/assets/${a!.assetId}/lock`, {
          json: { candidateId: vars.candidateId },
        })
        .json<BrainAsset>();
    },
    onSuccess: () => {
      toast.success('已切换锁定');
      qc.invalidateQueries({ queryKey: ['brain', 'asset', a!.assetId] });
      onChanged?.();
    },
    onError: (e: Error) => toast.error('切换锁定失败', e.message || '稍后重试'),
  });

  const deleteRunMut = useMutation({
    mutationFn: async (runId: string) => {
      return await api
        .delete(`brain/assets/${a!.assetId}/generations/${runId}`)
        .json<BrainAsset>();
    },
    onSuccess: () => {
      toast.success('已删除该轮');
      qc.invalidateQueries({ queryKey: ['brain', 'asset', a!.assetId] });
      onChanged?.();
    },
    onError: (e: Error) => toast.error('删除失败', e.message || '稍后重试'),
  });

  const renameMut = useMutation({
    mutationFn: async (name: string) => {
      return await api
        .patch(`brain/assets/${a!.assetId}`, { json: { name } })
        .json<BrainAsset>();
    },
    onSuccess: () => {
      toast.success('名称已更新');
      setEditingName(false);
      qc.invalidateQueries({ queryKey: ['brain', 'asset', a!.assetId] });
      onChanged?.();
    },
    onError: (e: Error) => toast.error('保存失败', e.message || '稍后重试'),
  });

  useEffect(() => {
    if (!editingName) setNameDraft(a?.name ?? '');
  }, [a?.name, editingName]);

  if (!open || !a) return null;
  const isCharacter = a.kind === 'character';
  const isProp = a.kind === 'prop';
  const labels = (isCharacter ? CHAR_LABELS : isProp ? PROP_LABELS : LOC_LABELS) as Record<string, string>;
  const generations = a.generations ?? [];

  const handleSwitchLock = (candidateId: string, currentlyLocked: boolean) => {
    if (currentlyLocked) return;
    setLockTarget(candidateId);
  };

  const handleDeleteRun = (runId: string, idx: number) => {
    setDeleteTarget({ runId, idx });
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[100] flex">
        <button
          type="button"
          aria-label="close"
          className="flex-1 cursor-default bg-black/30 backdrop-blur-sm"
          onClick={onClose}
        />
        <aside className="flex h-full w-full max-w-3xl flex-col overflow-hidden bg-surface shadow-m3">
          <header className="flex items-start justify-between gap-3 border-b border-outline-variant p-5">
            <div className="min-w-0 flex-1">
              <div className="mb-1 text-xs uppercase tracking-wide text-brand-500">
                {isCharacter ? '角色设定' : isProp ? '道具设定' : '场景设定'}
              </div>
              {editingName ? (
                <div className="flex items-center gap-1.5">
                  <Input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const v = nameDraft.trim();
                        if (v && v !== a.name) renameMut.mutate(v);
                        else setEditingName(false);
                      } else if (e.key === 'Escape') {
                        setEditingName(false);
                      }
                    }}
                    className="h-8 text-base font-semibold"
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    disabled={renameMut.isPending}
                    onClick={() => {
                      const v = nameDraft.trim();
                      if (v && v !== a.name) renameMut.mutate(v);
                      else setEditingName(false);
                    }}
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setEditingName(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <button
                  type="button"
                  className="group inline-flex items-center gap-1.5 rounded text-left hover:bg-surface-variant/60"
                  onClick={() => {
                    setNameDraft(a.name);
                    setEditingName(true);
                  }}
                  title="点击修改名称"
                >
                  <h2 className="text-lg font-semibold leading-tight text-neutral-800">{a.name}</h2>
                  <Pencil className="h-3.5 w-3.5 text-outline opacity-0 transition group-hover:opacity-100" />
                </button>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {a.status === 'locked' ? (
                  <Badge variant="success" className="gap-1">
                    <Lock className="h-3 w-3" />
                    已锁定
                  </Badge>
                ) : (
                  <Badge variant="warning">草稿</Badge>
                )}
                <span className="text-[11px] text-outline">
                  {generations.length} 轮生成
                </span>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </header>

          <div className="grid flex-1 grid-cols-5 overflow-hidden">
            <section className="col-span-3 space-y-4 overflow-y-auto border-r border-outline-variant p-5">
              <div>
                <div className="mb-2 text-xs font-medium text-neutral-500">锁定设定图</div>
                {a.lockedImageUrl ? (
                  <button
                    type="button"
                    className="group relative block w-full overflow-hidden rounded-md border border-outline-variant"
                    onClick={() => setLightboxUrl(a.lockedImageUrl!)}
                  >
                    <img
                      src={a.lockedImageUrl}
                      alt={a.name}
                      className="w-full object-contain"
                    />
                    <div className="absolute right-2 top-2 rounded-full bg-black/55 p-1.5 text-white opacity-0 transition group-hover:opacity-100">
                      <Maximize2 className="h-3.5 w-3.5" />
                    </div>
                  </button>
                ) : (
                  <div className="flex aspect-[4/5] items-center justify-center rounded-md bg-surface-variant text-outline">
                    <ImageIcon className="h-10 w-10" />
                  </div>
                )}
              </div>

              <div>
                <div className="mb-2 text-xs font-medium text-neutral-500">维度</div>
                <dl className="space-y-1.5 text-sm">
                  {Object.entries(labels).map(([k, label]) => {
                    const v = (a.attributes ?? {})[k as keyof typeof a.attributes];
                    if (v == null || (Array.isArray(v) && v.length === 0) || v === '') return null;
                    const display = Array.isArray(v) ? v.join('、') : String(v);
                    return (
                      <div key={k} className="grid grid-cols-[100px_1fr] gap-2">
                        <dt className="text-xs text-neutral-500">{label}</dt>
                        <dd className="text-sm text-neutral-800">{display}</dd>
                      </div>
                    );
                  })}
                </dl>
              </div>
            </section>

            <section className="col-span-2 space-y-4 overflow-y-auto p-5">
              <div className="text-xs font-medium text-neutral-500">历史生成</div>
              {generations.length === 0 ? (
                <div className="text-xs text-neutral-500">还没有生成记录。</div>
              ) : (
                <div className="space-y-3">
                  {[...generations].reverse().map((g, idx) => {
                    const roundNumber = generations.length - idx;
                    return (
                      <div
                        key={g.runId}
                        className="space-y-1.5 rounded-md border border-outline-variant p-2"
                      >
                        <div className="flex items-center justify-between text-[11px] text-neutral-500">
                          <span>第 {roundNumber} 轮 · {g.count} 张</span>
                          <div className="flex items-center gap-2">
                            <span>{new Date(g.createdAt).toLocaleString()}</span>
                            <button
                              type="button"
                              className="rounded p-0.5 text-neutral-400 hover:bg-danger-soft hover:text-danger disabled:opacity-50"
                              title="删除该轮"
                              disabled={deleteRunMut.isPending}
                              onClick={() => handleDeleteRun(g.runId, roundNumber)}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-1.5">
                          {g.candidates.map((c) => (
                            <div
                              key={c.id}
                              className={cn(
                                'group relative aspect-square overflow-hidden rounded',
                                c.picked && 'ring-2 ring-brand-500',
                              )}
                            >
                              {c.url ? (
                                <>
                                  <img
                                    src={c.url}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center gap-1.5 bg-black/55 opacity-0 transition group-hover:opacity-100">
                                    <button
                                      type="button"
                                      className="rounded-full bg-white/90 p-1.5 text-neutral-800 hover:bg-white"
                                      title="查看大图"
                                      onClick={() => setLightboxUrl(c.url!)}
                                    >
                                      <Maximize2 className="h-3 w-3" />
                                    </button>
                                    {!c.picked && (
                                      <button
                                        type="button"
                                        className="rounded-full bg-brand-500 p-1.5 text-white hover:bg-brand-600 disabled:opacity-50"
                                        title="切换为锁定"
                                        disabled={lockMut.isPending}
                                        onClick={() => handleSwitchLock(c.id, false)}
                                      >
                                        <Lock className="h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <div className="flex h-full w-full items-center justify-center bg-danger-soft text-[10px] text-danger">
                                  失败
                                </div>
                              )}
                              {c.picked && (
                                <div className="absolute right-0.5 top-0.5 rounded-full bg-brand-500 p-0.5 text-white">
                                  <Lock className="h-2.5 w-2.5" />
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>

          <footer className="flex items-center justify-end gap-2 border-t border-outline-variant p-4">
            <Button
              variant="agent"
              agentColor="brain"
              onClick={() => setRegenOpen(true)}
            >
              <RefreshCw className="h-3.5 w-3.5" />
              再来一轮
            </Button>
          </footer>
        </aside>
      </div>

      <Lightbox url={lightboxUrl} alt={a.name} onClose={() => setLightboxUrl(null)} />

      <ConfirmDialog
        open={!!lockTarget}
        onOpenChange={(v) => !v && setLockTarget(null)}
        title="切换锁定?"
        description="将这张图设为当前锁定的设定图,原锁定图将取消标记。"
        confirmText="切换锁定"
        pending={lockMut.isPending}
        onConfirm={() => {
          if (!lockTarget) return;
          const id = lockTarget;
          setLockTarget(null);
          lockMut.mutate({ candidateId: id });
        }}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title={deleteTarget ? `删除第 ${deleteTarget.idx} 轮?` : '删除该轮?'}
        description="该轮的所有候选图将一并移除,操作不可恢复。"
        confirmText="删除"
        tone="danger"
        pending={deleteRunMut.isPending}
        onConfirm={() => {
          if (!deleteTarget) return;
          const { runId } = deleteTarget;
          setDeleteTarget(null);
          deleteRunMut.mutate(runId);
        }}
      />

      <CreateAssetDialog
        open={regenOpen}
        kind={a.kind === 'prop' ? 'prop' : a.kind === 'location' ? 'location' : 'character'}
        initialAsset={a}
        onClose={() => setRegenOpen(false)}
        onCreated={() => {
          setRegenOpen(false);
          qc.invalidateQueries({ queryKey: ['brain', 'asset', a.assetId] });
          onChanged?.();
        }}
      />
    </>,
    document.body,
  );
}
